import PropTypes from 'prop-types'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  CollapsibleButton,
  CollapsibleHeading,
  CollapsibleItem,
  ToggleIcon
} from './styles'

const CollapsibleStates = {
  collapsed: 'COLLAPSED',
  open: 'OPEN'
}

const CollapsibleContext = createContext()
const CollapsibleItemContext = createContext()

const usePanel = (panelRef, context, explicitIndex) => {
  const { panels, registerPanel } = useContext(context)

  useEffect(() => {
    registerPanel(panelRef)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const createAutoIndex = panels => panels.findIndex(ref => ref === panelRef)

  return explicitIndex || createAutoIndex(panels)
}

const Collapsible = ({ children, as: Comp, defaultIndex, id, onChange }) => {
  const [openPanels, setOpenPanels] = useState(() => [defaultIndex])
  const [panels, setPanels] = useState([])

  const onSelectPanel = useCallback(index => {
    onChange && onChange(index)
    setOpenPanels(currentPanels => {
      if (currentPanels.includes(index)) {
        return []
      }
      return [index]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const registerPanel = useCallback(panelRef => {
    setPanels(() => {
      panels.push(panelRef)
      return [...new Set(panels)]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const context = useMemo(
    () => ({
      collapsibleId: id,
      onSelectPanel,
      openPanels,
      panels,
      registerPanel
    }),
    [openPanels, id, onSelectPanel, panels, registerPanel]
  )

  return (
    <CollapsibleContext.Provider value={context}>
      <Comp id={id} className='divide-y'>
        {children}
      </Comp>
    </CollapsibleContext.Provider>
  )
}

const Item = ({ children, as, index: explicitIndex, ...otherProps }) => {
  const { collapsibleId, openPanels } = useContext(CollapsibleContext)
  const itemRef = useRef(null)
  const index = usePanel(itemRef, CollapsibleContext, explicitIndex)
  const itemId = `${collapsibleId}-${index}`
  const panelId = `${collapsibleId}-panel-${index}`
  const buttonId = `${collapsibleId}-button-${index}`
  const isOpen = openPanels.includes(index)
    ? CollapsibleStates.open
    : CollapsibleStates.collapsed
  const context = {
    buttonId,
    index,
    itemId,
    itemRef,
    panelId,
    isOpen
  }

  return (
    <CollapsibleItemContext.Provider value={context}>
      <CollapsibleItem
        as={as}
        data-expanded={isOpen === CollapsibleStates.open}
        {...otherProps}
      >
        {children}
      </CollapsibleItem>
    </CollapsibleItemContext.Provider>
  )
}

const Header = ({ children, level }) => {
  return <CollapsibleHeading level={`h${level}`}>{children}</CollapsibleHeading>
}

const Button = ({ children, as: Comp, ...otherProps }) => {
  const { onSelectPanel, panels } = useContext(CollapsibleContext)
  const {
    buttonId,
    index,
    itemRef: ownRef,
    panelId,
    isOpen
  } = useContext(CollapsibleItemContext)

  const getPrevPanel = (currentIndex, panels) => {
    const prevIndex = currentIndex - 1
    if (prevIndex < 0) {
      return panels[panels.length - 1]
    }
    return panels[prevIndex]
  }

  const getNextPanel = (currentIndex, panels) => {
    const nextIndex = currentIndex + 1
    if (nextIndex >= panels.length) {
      return panels[0]
    }
    return panels[nextIndex]
  }

  const handleClick = e => {
    e.preventDefault()
    ownRef.current.focus()
    onSelectPanel(index)
  }

  const handleKeyDown = e => {
    const supportedKeys = ['ArrowDown', 'ArrowUp']
    const pressedKey = e.key

    if (!supportedKeys.includes(pressedKey)) {
      return
    }

    e.preventDefault()

    switch (pressedKey) {
      case 'ArrowDown': {
        const nextPanel = getNextPanel(index, panels)
        nextPanel.current && nextPanel.current.focus()
        break
      }
      case 'ArrowUp': {
        const prevPanel = getPrevPanel(index, panels)
        prevPanel.current.focus()
        break
      }
    }
  }

  return Comp ? (
    <Comp ref={ownRef} id={buttonId} onKeyDown={handleKeyDown} {...otherProps}>
      {children}
    </Comp>
  ) : (
    <CollapsibleButton
      ref={ownRef}
      id={buttonId}
      type='button'
      onClick={handleClick}
      aria-controls={panelId}
      aria-expanded={isOpen === CollapsibleStates.open}
      onKeyDown={handleKeyDown}
      {...otherProps}
    >
      {children}
      <ToggleIcon focusable='false' />
    </CollapsibleButton>
  )
}

const Panel = ({ children, as: Comp }) => {
  const { panelId, buttonId, isOpen } = useContext(CollapsibleItemContext)
  return (
    <Comp
      hidden={isOpen !== CollapsibleStates.open}
      id={panelId}
      aria-labelledby={buttonId}
      role='region'
      tabIndex={-1}
    >
      {children}
    </Comp>
  )
}

const noop = () => {}

const commonProps = {
  as: PropTypes.string,
  children: PropTypes.node
}

Collapsible.propTypes = {
  ...commonProps,
  defaultIndex: PropTypes.number,
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func
}

Collapsible.defaultProps = {
  as: 'div',
  defaultIndex: 0,
  onChange: noop
}

Item.propTypes = {
  ...commonProps
}

Item.defaultProps = {
  as: 'div'
}

Header.propTypes = {
  children: PropTypes.node,
  level: PropTypes.number,
  styleOverride: PropTypes.string
}

Header.defaultProps = {
  level: 2,
  styleOverride: ''
}

Button.propTypes = {
  as: PropTypes.object,
  children: PropTypes.node
}

Button.defaultProps = {
  as: null
}

Panel.propTypes = {
  ...commonProps
}

Panel.defaultProps = {
  as: 'div'
}

Collapsible.Item = Item
Collapsible.Header = Header
Collapsible.Button = Button
Collapsible.Panel = Panel

export { Collapsible }
